import { persistedQueryClient } from 'components/CacheableQueryClientProvider';
import { User } from 'shared/types';
import { isAuthorizedUser } from 'shared/utils';

/**
 * =============================================================================
 * Axios URL Authorized Utilities
 * =============================================================================
 *
 * These helpers are designed to work within an Axios request interceptor.
 * The `requestUrl` is expected to be a relative path from the Axios config
 * (e.g., 'geo_events/1'), not a full URL with a domain, query parameters,
 * or hash fragments.
 */

/**
 * Defines the route prefixes that have an authorized counterpart. Any URL
 * whose leading segments match one of these prefixes will be handled by
 * the authorization logic.
 *
 * Examples:
 * - `reports/[anything/else/...]`
 * - `geo_events/[anything/else/...]`
 */
const AUTHORIZED_ROUTE_PREFIXES = ['reports', 'geo_events'];

/**
 * Specific public endpoints that fall under an authorized prefix (and so would
 * otherwise be rewritten), but must stay on their public route.
 *
 * Each entry is matched as a full path (ignoring leading/trailing slashes), not
 * as a prefix. So `reports/photos` opts out only itself, while deeper paths like
 * `reports/photos/ring` remain subject to the normal authorized rewrite.
 */
const PUBLIC_ROUTE_EXCEPTIONS = ['reports/photos'];

/**
 * Splits a URL path into its non-empty segments.
 * Handles leading and trailing slashes.
 * e.g., '/reports/count/' -> ['reports', 'count']
 */
const getUrlSegments = (url: string): string[] => {
  if (!url) {
    return [];
  }
  return url.split('/').filter(Boolean);
};

/**
 * Returns true if `segments` begins with every segment in `routeSegments`,
 * compared position by position. Any extra trailing segments are ignored.
 */
const startsWithSegments = (
  segments: string[],
  routeSegments: string[],
): boolean => routeSegments.every((segment, i) => segments[i] === segment);

/**
 * Returns true if the URL's leading segments match any of the given route
 * prefixes. For example, the prefix 'geo_events' matches 'geo_events/1', and
 * 'reports/photos' would match 'reports/photos/1'.
 */
const matchesRoutePrefix = (
  segments: string[],
  routePrefixes: string[],
): boolean =>
  routePrefixes.some((prefix) =>
    startsWithSegments(segments, getUrlSegments(prefix)),
  );

/**
 * Returns true if the URL's segments exactly match any of the given routes.
 * Unlike matchesRoutePrefix, the segment counts must also be equal, so
 * 'reports/photos' matches only 'reports/photos' (ignoring leading/trailing
 * slashes), not 'reports/photos/123'.
 */
const matchesRouteExactly = (segments: string[], routes: string[]): boolean =>
  routes.some((route) => {
    const routeSegments = getUrlSegments(route);
    return (
      routeSegments.length === segments.length &&
      startsWithSegments(segments, routeSegments)
    );
  });

/**
 * Determines if a given request should use a special authorized endpoint.
 * Returns true only if all of the following conditions are met:
 * 1. The current user is authenticated and `authorized`.
 * 2. The URL's leading path segments match an entry in `AUTHORIZED_ROUTE_PREFIXES`.
 * 3. The URL does not exactly match a public exception in `PUBLIC_ROUTE_EXCEPTIONS`.
 */
export const shouldUseAuthorizedEndpoint = (requestUrl: string): boolean => {
  if (!requestUrl) {
    return false;
  }

  const authUser =
    persistedQueryClient.getQueryData<User>(['auth-user']) ?? null;

  if (!authUser || !isAuthorizedUser(authUser)) {
    return false;
  }

  const segments = getUrlSegments(requestUrl);

  return (
    matchesRoutePrefix(segments, AUTHORIZED_ROUTE_PREFIXES) &&
    !matchesRouteExactly(segments, PUBLIC_ROUTE_EXCEPTIONS)
  );
};

/**
 * Inserts 'authorized' after the first segment of a URL path while
 * preserving the original leading and trailing slashes. Empty or
 * root-only paths are returned unmodified.
 */
export const getAuthorizedUrl = (requestUrl: string): string => {
  const parts = getUrlSegments(requestUrl);
  if (parts.length === 0) return requestUrl;

  const authorized = [parts[0], 'authorized', ...parts.slice(1)].join('/');
  const prefix = requestUrl.startsWith('/') ? '/' : '';
  const suffix = requestUrl.endsWith('/') && requestUrl.length > 1 ? '/' : '';

  return `${prefix}${authorized}${suffix}`;
};
